const MANIFEST_PATH = "/asset-manifest.json";
const CHECK_INTERVAL_MS = 60_000;
const VERSION_STORAGE_KEY = "vendistri.appVersion";
let reloadRequested = false;

type AssetManifest = {
  files?: Record<string, string>;
};

const normalizedAssetPath = (value: string) => {
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value;
  }
};

export const loadedMainAsset = () => {
  const main = Array.from(document.scripts).find((script) =>
    /\/static\/js\/main\.[^/]+\.js$/.test(normalizedAssetPath(script.src))
  );
  return main?.src ? normalizedAssetPath(main.src) : null;
};

export const deployedMainAsset = (manifest: AssetManifest) => {
  const main = manifest.files?.["main.js"];
  return main ? normalizedAssetPath(main) : null;
};

const reloadForVersion = (version: string) => {
  if (reloadRequested) return;
  reloadRequested = true;
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
  window.location.reload();
};

export async function checkForNewAppVersion() {
  if (process.env.NODE_ENV !== "production") return;
  const loaded = loadedMainAsset();
  if (!loaded) return;

  try {
    const response = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;
    const deployed = deployedMainAsset((await response.json()) as AssetManifest);
    if (deployed && deployed !== loaded) reloadForVersion(deployed);
  } catch {
    // A transient network failure must never interrupt the running application.
  }
}

export function startAppVersionMonitor() {
  if (process.env.NODE_ENV !== "production") return () => {};

  const checkWhenVisible = () => {
    if (document.visibilityState === "visible") {
      void checkForNewAppVersion();
    }
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key === VERSION_STORAGE_KEY && event.newValue) {
      reloadForVersion(event.newValue);
    }
  };

  const interval = window.setInterval(checkWhenVisible, CHECK_INTERVAL_MS);
  window.addEventListener("focus", checkWhenVisible);
  window.addEventListener("online", checkWhenVisible);
  window.addEventListener("storage", handleStorage);
  document.addEventListener("visibilitychange", checkWhenVisible);
  checkWhenVisible();

  return () => {
    window.clearInterval(interval);
    window.removeEventListener("focus", checkWhenVisible);
    window.removeEventListener("online", checkWhenVisible);
    window.removeEventListener("storage", handleStorage);
    document.removeEventListener("visibilitychange", checkWhenVisible);
  };
}
